import { Link } from "react-router-dom";
import {
  Truck,
  Package,
  Sparkles,
  Calendar,
  Boxes,
  Home,
  Briefcase,
  Leaf,
  Recycle,
  RotateCcw,
  ArrowRight,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison à domicile à Chavenay ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Livraison à domicile
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Livraison de cartons assurée à domicile {city.aCity}
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Les semaines qui précèdent un changement d&apos;adresse
              exigent une organisation rigoureuse. C&apos;est pourquoi
              notre service de proximité prend en charge
              l&apos;intégralité du transport de vos fournitures pour
              vous soulager au quotidien.
            </p>
          </div>

          <div className="lg:col-span-5 space-y-4">
            {[
              {
                title: "Livraison sur-mesure",
                body: "Nous acheminons votre commande à plat directement à votre porte à Chavenay, ainsi que dans les localités voisines comme Villepreux, Saint-Nom-la-Bretèche, Les Clayes-sous-Bois et Feucherolles.",
                Icon: Truck,
              },
              {
                title: "Confort et sérénité",
                body: "Vous recevez des produits propres, secs et prêts à être dépliés selon votre propre calendrier. Vous vous évitez la corvée de charger des paquets encombrants dans votre coffre et optimisez votre précieux temps.",
                Icon: Calendar,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="flex items-start gap-4 bg-white border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
              >
                <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <div>
                  <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                    {title}
                  </h3>
                  <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                    {body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Particuliers & professionnels ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Particuliers &amp; professionnels
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Des fournitures professionnelles adaptées à chaque projet
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Chaque projet de transfert possède ses propres spécificités
            techniques, qu&apos;il s&apos;agisse de vider une maison de
            famille ou de déplacer des bureaux d&apos;entreprise.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {[
            {
              title: "Pour les particuliers",
              body: "Cartons de tailles variées, modèles renforcés pour vos charges lourdes, formats compacts spécial livres pour préserver votre dos, ainsi que des penderies pour vos vêtements sur cintres.",
              Icon: Home,
            },
            {
              title: "Pour les professionnels",
              body: "Cartons d'archivage d'une grande robustesse pour vos documents, films étirables techniques pour solidariser vos palettes et protections adaptées pour sécuriser votre matériel informatique.",
              Icon: Briefcase,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
            >
              <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Icon className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div>
                <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>

        <p className="mt-8 max-w-3xl text-[15px] md:text-base text-[#3a4856] leading-relaxed">
          Cette polyvalence vous garantit de trouver une réponse exacte
          à vos besoins, quel que soit le volume à transférer. En
          centralisant vos achats chez un fournisseur unique capable de
          s&apos;adapter aux exigences des entreprises comme à celles
          des familles, vous gagnez en efficacité et vous vous assurez
          d&apos;utiliser un matériel validé par les experts du secteur
          pour sécuriser chaque mètre cube de vos volumes.
        </p>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-chavenay-cta-particuliers"
          >
            <Home className="h-4 w-4" strokeWidth={2.6} />
            Voir la gamme particuliers
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/cartons-demenagement?categorie=pro"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-chavenay-cta-pro"
          >
            <Briefcase className="h-4 w-4" strokeWidth={2.6} />
            Solutions professionnelles
          </Link>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Emballages recyclables ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Éco-conception
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Des emballages conçus pour être recyclés après utilisation
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Parce que la réussite d&apos;un déménagement moderne va de
              pair avec le respect de notre environnement, nous portons
              une attention particulière à la composition de nos
              produits.
            </p>

            <p className="mt-6 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              En optant pour ces solutions durables, vous faites un choix
              responsable pour la planète tout en bénéficiant d&apos;une
              sécurité maximale pour vos objets. Une fois installé dans
              votre nouveau foyer {city.aCity}, le traitement de vos
              déchets devient simple, propre et parfaitement fluide.
            </p>

            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/cartons-reconditionnes"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
                data-testid="city-chavenay-cta-recycle"
              >
                <Recycle className="h-4 w-4" strokeWidth={2.6} />
                Découvrir les cartons reconditionnés
                <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
              </Link>
              <Link
                to="/cartons-demenagement"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
                data-testid="city-chavenay-cta-shop"
              >
                <Boxes className="h-4 w-4" strokeWidth={2.6} />
                Toute la gamme
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5 space-y-4">
            {[
              {
                title: "Matériaux éco-conçus",
                body: "L'ensemble de nos cartons est fabriqué à partir de fibres de cellulose recyclées de haute qualité et utilise des colles naturelles, garantissant qu'ils sont 100% recyclables et biodégradables.",
                Icon: Leaf,
              },
              {
                title: "Seconde vie garantie",
                body: "Grâce à leur épaisseur professionnelle, ces emballages ne se détériorent pas après un seul voyage. Vous pouvez aisément les replier pour les stocker, les transmettre à des proches ou les confier aux filières de tri locales.",
                Icon: RotateCcw,
              },
              {
                title: "Filière responsable",
                body: "Une démarche complète qui réduit drastiquement les déchets d'emballage à usage unique sur le territoire des Yvelines.",
                Icon: Sparkles,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
              >
                <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <div>
                  <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                    {title}
                  </h3>
                  <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                    {body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-t border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-chavenay-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
